// The commands of a list of unsent drafts (the Review view's, see
// components/DraftTriage): the cursor over the drafts, and the verbs on the
// draft it is on.
//
// The verbs act only while the focus is in the list, so no draft is
// ticked or discarded unseen: from elsewhere on the page a verb's key is
// left to the page (or to the scope around the list). A motion works from
// anywhere: the first one enters the list at the cursor.

import { times, type Command } from './command';
import { leave, nothingFor, pass, type Update } from './effects';

/** The list's own model: the draft its cursor is on. */
export interface DraftsState {
  cursor: string | null;
}

/** What the list shows and does not own, as it is when a message is sent. */
export interface DraftsEnv {
  /** The drafts in the order listed. */
  drafts: readonly string[];
  /** The focus is in the list. */
  inList: boolean;
}

/** A command, or the cursor put on a draft outright (a click on it, or the list changing under it). */
export type DraftsMsg = Command | { kind: 'rest'; id: string | null };

type DraftsModel = DraftsState & DraftsEnv;

export type DraftsEffect =
  /** Show the cursor's draft and give it the focus. */
  | { kind: 'show'; id: string }
  /** Tick the draft, or untick a ticked one. `advance`: after a tick the cursor moves to the next draft; without it the cursor rests on this one and nothing scrolls. */
  | { kind: 'toggle'; id: string; advance: boolean }
  | { kind: 'edit'; id: string }
  | { kind: 'discard'; id: string }
  /** Open the draft in Files. */
  | { kind: 'jump'; id: string };

const HERE = 'in the list of unsent drafts';

function act(m: DraftsModel, command: Extract<Command, { kind: 'act' }>): Update<DraftsModel, DraftsEffect> {
  const named = command.at?.object === 'draft' ? command.at.id : null;
  if (command.at && named === null) return [m, [pass]];
  // A draft named outright (its own control) is acted on wherever the focus is; the cursor's only from inside the list.
  const id = named ?? (m.inList ? m.cursor : null);
  const d = id !== null && m.drafts.includes(id) ? id : null;
  switch (command.verb) {
    // These two keys have a meaning of their own elsewhere on the page (a scroll, a button).
    case 'include':
      return [m, [d ? { kind: 'toggle', id: d, advance: command.advance === true } : leave]];
    case 'open':
      return [m, [d ? { kind: 'jump', id: d } : leave]];
    case 'edit':
    case 'discard':
      return [m, [d ? { kind: command.verb, id: d } : pass]];
    case 'review':
    case 'unmark':
    case 'comment':
    case 'copy':
      return [m, [pass]];
  }
}

/** The list's update: the state that comes back is its model (the same object when the cursor did not move). */
export function updateDrafts(state: DraftsState, env: DraftsEnv, msg: DraftsMsg): Update<DraftsState, DraftsEffect> {
  if (msg.kind === 'rest') return [msg.id === state.cursor ? state : { cursor: msg.id }, []];
  const [next, effects] = step({ ...env, ...state }, msg);
  return [next.cursor === state.cursor ? state : { cursor: next.cursor }, effects];
}

function step(m: DraftsModel, command: Command): Update<DraftsModel, DraftsEffect> {
  switch (command.kind) {
    case 'move': {
      if (command.object !== 'draft') return [m, [pass]];
      if (command.only || !m.drafts.length) return [m, [nothingFor(command, HERE)]];
      const at = Math.max(0, m.cursor === null ? -1 : m.drafts.indexOf(m.cursor));
      // From outside the list, the first motion only enters it, at the cursor's draft.
      const id = m.drafts[m.inList ? Math.min(m.drafts.length - 1, Math.max(0, at + command.dir * times(command))) : at];
      return [{ ...m, cursor: id }, [{ kind: 'show', id }]];
    }
    case 'act':
      return act(m, command);
    case 'scroll':
    case 'select':
    case 'range':
    case 'grow':
    case 'fold':
    case 'present':
    case 'goto':
    case 'open':
    case 'refresh':
    case 'submit':
    case 'cancel':
      return [m, [pass]];
  }
}
